"use client";

import React from "react";
import { DateTime } from "luxon";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiClient } from "@/lib/apiClient";
import { useCalendar } from "@/contexts/CalendarContext";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import {
  useWaitlistMatches,
  useWaitlistRefetch,
  WaitlistMatch,
} from "@/hooks/calendar/useWaitlistMatches";
import {
  useWaitlistCatalog,
  WaitlistCatalogService,
} from "@/hooks/calendar/useWaitlistCatalog";

/**
 * Waiting-list content for the notifications sliding panel (README §4.6).
 * Was a standalone TopBar popup (WaitlistBell); consolidated into the bell
 * panel as a tab. Data comes from the shared react-query cache — toasts are
 * driven once from the always-mounted sidebar instance, so this instance
 * reads without re-firing them.
 *
 * `onOpenAppointment` closes the panel; Book now jumps the calendar to the
 * slot's date and points the admin at the new-appointment action.
 */
export default function WaitlistTabContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { data } = useWaitlistMatches(true, { withToasts: false });
  const refetch = useWaitlistRefetch();
  const { setDate } = useCalendar();
  const [showAdd, setShowAdd] = React.useState(false);

  const matches = data?.matches ?? [];
  const waiting = data?.waiting ?? [];

  const bookNow = (m: WaitlistMatch) => {
    const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
    setDate(DateTime.fromISO(m.slot.date, { zone: tz }));
    onClose();
    toast.info(
      t("waitlist.book_now_hint",
        "Calendar jumped to {{date}} — book {{client}} with {{master}} at {{start}} ({{phone}})",
        { date: m.slot.date, client: m.entry.client_name,
          master: m.slot.staff_name ?? "", start: m.slot.start,
          phone: m.entry.client_phone }),
      { duration: 12000 });
  };

  const saveNote = async (id: number) => {
    const note = window.prompt(
      t("waitlist.note_prompt", "Note (e.g. called, will come Tuesday):"),
      t("waitlist.note_default", "contacted"));
    if (note === null) return;
    try {
      await apiClient.post(`/api/v1/company/waitlist/entries/${id}/note`, { note });
      refetch();
      toast.success(t("waitlist.note_saved", "Note saved: {{note}}", { note }));
    } catch {
      toast.error(t("waitlist.action_failed", "Action failed"));
    }
  };

  const cancelEntry = async (id: number) => {
    if (!window.confirm(t("waitlist.remove_confirm", "Remove this client from the waiting list?"))) return;
    try {
      await apiClient.post(`/api/v1/company/waitlist/entries/${id}/cancel`);
      refetch();
    } catch {
      toast.error(t("waitlist.action_failed", "Action failed"));
    }
  };

  return (
    // Own scroll region rather than relying on the panel's wrapper alone: the
    // entries list scrolls while the counter stays put and the add-client
    // action stays reachable, so a long waiting list is fully browsable.
    // (min-h-0 on the scrolling child is required — a flex item will not
    // shrink below its content height without it.)
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-4 pb-2 pt-4 text-xs text-muted-foreground">
        {t("waitlist.active_n", "{{n}} waiting", { n: data?.active_count ?? 0 })}
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 pb-2">
        {matches.length > 0 && (
          <div className="text-xs font-semibold text-emerald-700">
            {t("waitlist.matches_now", "Can be booked right now")}
          </div>
        )}
        {matches.map((m) => (
          <div key={`${m.entry.id}-${m.slot.date}-${m.slot.start}`}
               className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2">
            <div className="text-sm font-medium">
              {m.entry.client_name}
              <span className="ml-1 text-xs text-muted-foreground">{m.entry.client_phone}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {m.entry.service_title}
              {m.entry.duration_min ? ` (${m.entry.duration_min} ${t("waitlist.min", "min")})` : ""}
              {" · "}{m.slot.staff_name} · {m.slot.date} {m.slot.start}
              {m.entry.notify_count > 0 && (
                <span className="ml-1">✉×{m.entry.notify_count}</span>
              )}
              {m.entry.note && <span className="ml-1 italic">({m.entry.note})</span>}
            </div>
            <div className="text-[11px] text-muted-foreground/80">
              {t("waitlist.added_by", "Added by")}:{" "}
              {m.entry.created_by_id
                ? (m.entry.created_by_name ?? `#${m.entry.created_by_id}`)
                : t("waitlist.created_by_client", "Client")}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <Button size="sm" className="h-7 rounded-lg text-xs" onClick={() => bookNow(m)}>
                {t("waitlist.book_now", "Book now")}
              </Button>
              <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs"
                      title={t("waitlist.save_note_hint", "Save a note on this entry, e.g. called the client")}
                      onClick={() => saveNote(m.entry.id)}>
                {t("waitlist.comment", "Comment")}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs text-destructive"
                      onClick={() => cancelEntry(m.entry.id)}>
                {t("waitlist.remove", "Remove")}
              </Button>
            </div>
          </div>
        ))}

        {waiting.length > 0 && (
          <div className="pt-1 text-xs font-semibold text-muted-foreground">
            {t("waitlist.still_waiting", "Still waiting for a slot")}
          </div>
        )}
        {waiting.map((e) => (
          <div key={e.id} className="rounded-lg border p-2">
            <div className="text-sm">{e.client_name}
              <span className="ml-1 text-xs text-muted-foreground">{e.client_phone}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {e.service_title}
              {e.duration_min ? ` (${e.duration_min} ${t("waitlist.min", "min")})` : ""}
              {" · "}{mastersLabel(e, t)} ·
              {" "}{e.date_from} — {e.date_to}
              {e.note && <span className="ml-1 italic">({e.note})</span>}
            </div>
            <div className="text-[11px] text-muted-foreground/80">
              {t("waitlist.added_by", "Added by")}:{" "}
              {e.created_by_id
                ? (e.created_by_name ?? `#${e.created_by_id}`)
                : t("waitlist.created_by_client", "Client")}
            </div>
            <div className="mt-1 flex gap-1.5">
              <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs"
                      title={t("waitlist.save_note_hint", "Save a note on this entry, e.g. called the client")}
                      onClick={() => saveNote(e.id)}>
                {t("waitlist.comment", "Comment")}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs text-destructive"
                      onClick={() => cancelEntry(e.id)}>
                {t("waitlist.remove", "Remove")}
              </Button>
            </div>
          </div>
        ))}

        {matches.length === 0 && waiting.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {t("waitlist.empty", "Nobody is waiting right now.")}
          </div>
        )}
      </div>

      {/* Pinned below the list. The add form is tall (10+ fields), so this
          section scrolls on its own and is capped at 65% of the panel —
          it can never squeeze the entries list out of view. While open it
          sits on a tinted surface with its own header, so the form reads as
          a distinct panel instead of blending into the entries list above. */}
      <div className={`max-h-[65%] shrink-0 overflow-y-auto border-t px-4 py-3 ${
        showAdd ? "bg-muted/60" : "bg-background"}`}>
        {showAdd ? (
          <>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-muted-foreground">
                {t("waitlist.add_entry_title", "New waiting-list entry")}
              </span>
              {/* Collapse — the form used to be a one-way door: once open the
                  only way out was submitting it. */}
              <Button size="sm" variant="ghost"
                      className="h-6 w-6 shrink-0 rounded-md p-0"
                      aria-label={t("waitlist.collapse_add", "Collapse the form")}
                      title={t("waitlist.collapse_add", "Collapse the form")}
                      onClick={() => setShowAdd(false)}>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
            <AddEntryForm onDone={() => { setShowAdd(false); refetch(); }} />
          </>
        ) : (
          <Button size="sm" variant="outline" className="w-full rounded-lg"
                  onClick={() => setShowAdd(true)}>
            {t("waitlist.add_entry", "+ Add client (phone-in)")}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * react-day-picker v9 classNames for the compact waitlist picker (the shared
 * ui/calendar.tsx is on the v9 key set too — this is just a denser variant):
 * nav pinned to the TOP row flanking the centered month label, 32px cells →
 * the whole grid is ~240px wide and fits the 360px Sheet easily.
 */
const WL_CAL_BTN =
  "inline-flex h-7 w-7 items-center justify-center rounded-md border " +
  "border-input bg-transparent opacity-60 hover:opacity-100 z-10";

const WL_CALENDAR_CLASSNAMES = {
  months: "relative flex flex-col",
  month: "space-y-3",
  month_caption: "flex h-7 items-center justify-center",
  caption_label: "text-sm font-medium capitalize",
  nav: "absolute inset-x-0 top-0 flex h-7 items-center justify-between",
  button_previous: WL_CAL_BTN,
  button_next: WL_CAL_BTN,
  month_grid: "w-full border-collapse",
  weekdays: "flex",
  weekday: "w-8 text-center text-[0.7rem] font-normal text-muted-foreground",
  week: "mt-1.5 flex w-full",
  day: "relative h-8 w-8 rounded-md p-0 text-center text-sm",
  day_button:
    "h-8 w-8 rounded-md font-normal hover:bg-accent hover:text-accent-foreground",
  selected:
    "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary",
  today: "[&>button]:bg-accent [&>button]:text-accent-foreground",
  outside: "text-muted-foreground opacity-40",
  disabled: "text-muted-foreground opacity-40",
  hidden: "invisible",
  chevron: "h-4 w-4",
};

/**
 * Date field for the add-entry form. Uses a portaled Popover + Calendar
 * (NOT a native <input type="date">, whose browser popup gets clipped and
 * mispositioned inside the sliding notifications Sheet) and is pinned to
 * open UPWARD always — `side="top"` + `avoidCollisions={false}` — so it is
 * never clipped at the bottom of the panel on short screens. `align` keeps
 * each popover inside the panel: "start" for the From field, "end" for the
 * To field (its right edge stays at the button's right edge instead of
 * running past the panel/viewport). Value is the ISO "YYYY-MM-DD" the
 * form/API already use; luxon converts both ways without the UTC
 * off-by-one of Date.toISOString().
 */
function DateField({ value, onChange, ariaLabel, align = "start" }: {
  value: string; onChange: (iso: string) => void; ariaLabel: string;
  align?: "start" | "end";
}) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? DateTime.fromISO(value).toJSDate() : undefined;
  const label = value ? DateTime.fromISO(value).toFormat("dd.MM.yyyy") : ariaLabel;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" aria-label={ariaLabel}
                className="h-8 flex-1 justify-start text-xs font-normal">
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align} side="top"
                      avoidCollisions={false}>
        <Calendar mode="single" selected={selected}
                  className="p-2"
                  classNames={WL_CALENDAR_CLASSNAMES}
                  components={{
                    Chevron: ({ orientation }) =>
                      orientation === "left"
                        ? <ChevronLeft className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />,
                  }}
                  onSelect={(d) => {
                    if (!d) return;
                    onChange(DateTime.fromJSDate(d).toISODate() ?? "");
                    setOpen(false);
                  }} />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The form's dropdowns use the portaled Radix Select, NOT a native <select>:
 * inside the sliding notifications Sheet a native popup gets clipped at the
 * panel edge and long lists (staff, services) become unreachable — the same
 * reason DateField above avoids the native date picker. Radix portals the
 * list to the body, flips on collision, and scrolls internally.
 * These classes keep it on the form's dense h-8 / text-xs rhythm.
 */
const WL_SEL_TRIGGER = "h-8 w-full px-2 text-xs";
const WL_SEL_ITEM = "py-1.5 pl-8 pr-2 text-xs";
// Cap to the viewport so a long list never runs past the screen; the
// SelectContent scroll buttons take over from there.
const WL_SEL_CONTENT = "max-h-[40vh]";

/** "Anna" (pinned) / "Anna, Maria" (admin subset) / "any master". */
function mastersLabel(
  e: { staff_name?: string | null; staff_names?: string[] | null },
  t: (k: string, d: string) => string,
): string {
  if (e.staff_name) return e.staff_name;
  if (e.staff_names?.length) return e.staff_names.join(", ");
  return t("waitlist.any_master", "any master");
}

/** 5400 → "1 h 30 min"; 1800 → "30 min"; 0 → "". */
function fmtDuration(sec: number, t: (k: string, d: string) => string): string {
  const total = Math.round((sec || 0) / 60);
  if (total <= 0) return "";
  const h = Math.floor(total / 60), m = total % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h} ${t("waitlist.hr", "hr")}`);
  if (m || !h) parts.push(`${m} ${t("waitlist.min", "min")}`);
  return parts.join(" ");
}

function AddEntryForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = React.useState({
    client_name: "", client_phone: "", client_email: "",
    service_id: 0,
    date_from: today, date_to: today, time_pref: "any", note: "",
  });
  // Checked masters — the admin's subset of the eligible list (README §2
  // staff_ids). All eligible masters are checked whenever the service
  // changes; unchecking removes one. Sent as `staff_ids`; the backend
  // collapses a single id to staff_id and the full set to "any".
  const [staffIds, setStaffIds] = React.useState<number[]>([]);
  // Duration picker (hours + minutes), mirroring the appointment modal's
  // Duration input. Preset from the selected service's own length (or the
  // single checked master's own length); only sent to the backend when the
  // admin changes it off that default (§4.2).
  const [duration, setDuration] = React.useState({ hours: 0, minutes: 0 });
  const [busy, setBusy] = React.useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  // Service FIRST (README §4.6, 2026-08-22): one catalog document carries
  // every active service plus, per service, the masters the matcher could
  // offer with their per-master effective duration — computed server-side
  // with the engine's own helpers, so the list can never disagree with
  // what the matcher will hunt for.
  const { data: catalog, isLoading: catalogLoading } = useWaitlistCatalog();
  const services = React.useMemo(() => catalog?.services ?? [], [catalog]);

  // Prefill the service once the catalog loads: the first one that has at
  // least one eligible master (falls back to the first service).
  React.useEffect(() => {
    if (form.service_id || !services.length) return;
    const first = services.find((svc) => svc.staff.length) ?? services[0];
    set("service_id", first.id);
  }, [services, form.service_id]);

  const selectedService: WaitlistCatalogService | undefined = React.useMemo(
    () => services.find((svc) => svc.id === Number(form.service_id)),
    [services, form.service_id]);
  const eligible = selectedService?.staff ?? [];

  // Service change ⇒ every eligible master checked (the owner's rule: "all
  // checked, with the ability to remove"). Keyed on the service id ONLY —
  // not on the catalog object — so a background refetch never resets the
  // admin's unchecks mid-edit. (The service id is set from the loaded
  // catalog, so `eligible` is already populated when this runs.)
  React.useEffect(() => {
    setStaffIds(eligible.map((m) => m.id));
  }, [form.service_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStaff = (id: number, checked: boolean) =>
    setStaffIds((ids) => checked
      ? (ids.includes(id) ? ids : [...ids, id])
      : ids.filter((x) => x !== id));
  const allChecked = eligible.length > 0 && staffIds.length === eligible.length;

  // Baseline for the duration picker: with exactly one master checked it is
  // THAT master's effective length (mirrors the old master-first form);
  // otherwise the service default — per-master differences are applied by
  // the matcher itself, the list shows them so the admin knows.
  const defaultDurationMin = React.useMemo(() => {
    if (!selectedService) return 0;
    const sec = staffIds.length === 1
      ? (eligible.find((m) => m.id === staffIds[0])?.duration_sec
         ?? selectedService.duration_sec)
      : selectedService.duration_sec;
    return Math.round((sec || 0) / 60);
  }, [selectedService, eligible, staffIds]);

  // Preset the hour/minute dropdowns to the baseline whenever it changes
  // (picking a different service / narrowing to one master re-baselines).
  React.useEffect(() => {
    setDuration({
      hours: Math.floor(defaultDurationMin / 60),
      minutes: defaultDurationMin % 60,
    });
  }, [defaultDurationMin]);

  const canSubmit = !busy && !!form.service_id && staffIds.length > 0
    && !!form.client_name.trim() && !!form.client_phone.trim();

  const submit = async () => {
    if (!form.service_id) {
      toast.error(t("waitlist.need_service", "Please choose a service"));
      return;
    }
    if (!staffIds.length) {
      toast.error(t("waitlist.need_master", "Keep at least one master selected"));
      return;
    }
    setBusy(true);
    try {
      const totalMin = duration.hours * 60 + duration.minutes;
      await apiClient.post("/api/v1/company/waitlist/entries", {
        ...form,
        // the checked subset; the backend normalizes (1 ⇒ staff_id,
        // all eligible ⇒ any master, otherwise the list)
        staff_ids: staffIds,
        client_email: form.client_email || undefined,
        // send the length only when the admin changed it off the baseline;
        // unchanged => omit => backend uses each master's own length
        duration_min:
          totalMin > 0 && totalMin !== defaultDurationMin ? totalMin : undefined,
        note: form.note.trim() || undefined,
      });
      onDone();
    } catch (e: unknown) {
      const ax = e as import("axios").AxiosError<any>;
      toast.error(String(ax?.response?.data?.error ?? t("waitlist.action_failed", "Action failed")));
    } finally {
      setBusy(false);
    }
  };

  // Group the service dropdown by category (the catalog is already ordered
  // category → weight).
  const groups = React.useMemo(() => {
    const out: { id: number; title: string; items: WaitlistCatalogService[] }[] = [];
    for (const svc of services) {
      let g = out.find((x) => x.id === svc.category_id);
      if (!g) { g = { id: svc.category_id, title: svc.category_title, items: [] }; out.push(g); }
      g.items.push(svc);
    }
    return out;
  }, [services]);

  return (
    <div className="space-y-1.5">
      <Input placeholder={t("waitlist.f_name", "Client name")} value={form.client_name}
             onChange={(e) => set("client_name", e.target.value)} className="h-8" />
      <Input placeholder={t("waitlist.f_phone", "Phone (+...)")} value={form.client_phone}
             onChange={(e) => set("client_phone", e.target.value)} className="h-8" />
      <Input placeholder={t("waitlist.f_email", "Email (optional — auto-notify)")} value={form.client_email}
             onChange={(e) => set("client_email", e.target.value)} className="h-8" />

      {/* Service first — compulsory; grouped by category */}
      <label className="block text-[11px] text-muted-foreground">{t("waitlist.f_service", "Service")}</label>
      <Select value={form.service_id ? String(form.service_id) : undefined}
              disabled={catalogLoading || !services.length}
              onValueChange={(v) => set("service_id", Number(v))}>
        <SelectTrigger className={WL_SEL_TRIGGER}>
          <SelectValue placeholder={
            catalogLoading
              ? t("waitlist.loading", "Loading…")
              : !services.length
                ? t("waitlist.no_services", "No active services")
                : t("waitlist.f_service", "Service")} />
        </SelectTrigger>
        <SelectContent className={WL_SEL_CONTENT}>
          {groups.map((g) => (
            <SelectGroup key={g.id}>
              {g.title && (
                <SelectLabel className="px-2 py-1 text-[11px] font-semibold text-muted-foreground">
                  {g.title}
                </SelectLabel>
              )}
              {g.items.map((svc) => (
                <SelectItem key={svc.id} value={String(svc.id)} className={WL_SEL_ITEM}>
                  {svc.title}
                  {svc.duration_sec > 0 && (
                    <span className="ml-1.5 text-[11px] text-muted-foreground">
                      {fmtDuration(svc.duration_sec, t)}
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>

      {/* Masters second — every eligible master for the service, all checked;
          uncheck to exclude. Each row shows THAT master's length for the
          service (per-master overrides), which is what the matcher uses. */}
      {selectedService && (
        <>
          <div className="flex items-center justify-between">
            <label className="text-[11px] text-muted-foreground">
              {t("waitlist.f_masters", "Masters")}
              {eligible.length > 0 && (
                <span className="ml-1">
                  {t("waitlist.masters_checked", "({{n}} of {{total}})",
                     { n: staffIds.length, total: eligible.length })}
                </span>
              )}
            </label>
            {eligible.length > 1 && !allChecked && (
              <button type="button"
                      className="text-[11px] text-primary underline-offset-2 hover:underline"
                      onClick={() => setStaffIds(eligible.map((m) => m.id))}>
                {t("waitlist.masters_all", "Select all")}
              </button>
            )}
          </div>
          {eligible.length === 0 ? (
            <div className="rounded-md border border-dashed px-2 py-1.5 text-[11px] text-muted-foreground">
              {t("waitlist.no_eligible_masters",
                 "No master can be booked online for this service — enable online booking in a staff profile first.")}
            </div>
          ) : (
            <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border px-2 py-1.5">
              {eligible.map((m) => {
                const checked = staffIds.includes(m.id);
                return (
                  <label key={m.id}
                         className="flex cursor-pointer items-center gap-2 py-0.5 text-xs">
                    <Checkbox checked={checked}
                              aria-label={m.name}
                              onCheckedChange={(v) => toggleStaff(m.id, v === true)} />
                    <span className={`min-w-0 flex-1 truncate ${checked ? "" : "text-muted-foreground line-through"}`}>
                      {m.name}
                      {/* Two masters can share a first name — the specialization
                          tells them apart (same as the appointment modal). */}
                      {m.specialization && (
                        <span className="ml-1 text-[11px] text-muted-foreground no-underline">
                          ({m.specialization})
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
                          title={t("waitlist.master_duration_hint",
                                   "This master's own length for the service")}>
                      {fmtDuration(m.duration_sec, t)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Dates — popover calendars pinned to open upward (never clipped);
          From anchors left, To anchors right so both stay inside the panel */}
      <div className="flex gap-1.5">
        <DateField value={form.date_from} onChange={(v) => set("date_from", v)}
                   ariaLabel={t("waitlist.f_date_from", "From")} align="start" />
        <DateField value={form.date_to} onChange={(v) => set("date_to", v)}
                   ariaLabel={t("waitlist.f_date_to", "To")} align="end" />
      </div>

      {/* Time of day */}
      <Select value={form.time_pref} onValueChange={(v) => set("time_pref", v)}>
        <SelectTrigger className={WL_SEL_TRIGGER}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className={WL_SEL_CONTENT}>
          <SelectItem value="any" className={WL_SEL_ITEM}>{t("waitlist.pref_any", "Any time")}</SelectItem>
          <SelectItem value="morning" className={WL_SEL_ITEM}>{t("waitlist.pref_morning", "Morning")}</SelectItem>
          <SelectItem value="afternoon" className={WL_SEL_ITEM}>{t("waitlist.pref_afternoon", "Afternoon")}</SelectItem>
          <SelectItem value="evening" className={WL_SEL_ITEM}>{t("waitlist.pref_evening", "Evening")}</SelectItem>
        </SelectContent>
      </Select>

      {/* Duration — hours + minutes, same shape as the appointment modal's
          Duration input. Preset to the service default (or the single checked
          master's own length); the admin nudges it to hold a longer/shorter
          slot. Only a change off the baseline is sent (see submit). */}
      <label className="block text-[11px] text-muted-foreground">{t("waitlist.f_duration_label", "Duration")}</label>
      <div className="grid grid-cols-2 gap-1.5">
        <Select value={String(duration.hours)}
                onValueChange={(v) => setDuration((d) => ({ ...d, hours: Number(v) }))}>
          <SelectTrigger className={WL_SEL_TRIGGER}
                         aria-label={t("waitlist.f_duration_hours", "Duration hours")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={WL_SEL_CONTENT}>
            {Array.from({ length: 13 }, (_, i) => (
              <SelectItem key={i} value={String(i)} className={WL_SEL_ITEM}>
                {i} {t("waitlist.hr", "hr")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(duration.minutes)}
                onValueChange={(v) => setDuration((d) => ({ ...d, minutes: Number(v) }))}>
          <SelectTrigger className={WL_SEL_TRIGGER}
                         aria-label={t("waitlist.f_duration_minutes", "Duration minutes")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={WL_SEL_CONTENT}>
            {/* 5-min steps, plus the current value so an odd service default
                (e.g. a 20-min service reads 20) always has a matching option */}
            {[...new Set([...Array.from({ length: 12 }, (_, i) => i * 5), duration.minutes])]
              .sort((a, b) => a - b)
              .map((mVal) => (
                <SelectItem key={mVal} value={String(mVal)} className={WL_SEL_ITEM}>
                  {mVal} {t("waitlist.min", "min")}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Comment captured at creation (previously only editable afterwards) */}
      <Input placeholder={t("waitlist.f_comment", "Comment (optional)")} value={form.note}
             onChange={(e) => set("note", e.target.value)} className="h-8" />

      <Button size="sm" className="w-full rounded-lg" disabled={!canSubmit} onClick={submit}>
        {t("waitlist.add", "Add to waiting list")}
      </Button>
    </div>
  );
}
